import { Icon } from '@iconify/react'
import React, { useEffect, useState } from 'react'
import {
  withStyles,
  Typography,
  Popover,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core'
import styles from './styles'
import { JsonFormEngine } from '@minicoursegenerator/json-form-engine'
import {
  CoreToggleButtonGroup,
  CoreToggleButton,
  CoreSlider,
  CoreBackgroundPosition,
} from 'src/components/core'
import { ChromePicker } from 'react-color'
import { track } from '@amplitude/analytics-browser'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import ContainerMediaGalery from 'src/container/image-gallery'
import { useDispatch, useSelector } from 'react-redux'
import CustomizedSwitch from 'src/components/customized-switch'
import {
  SizeType,
  handleAlignment,
  handleBackground,
  handleSize,
  handleImageDelete,
  handleOverlayColor,
  handlePosition,
  handleColor,
  handleImageOverlay,
  handleButtonWidth,
  handleContainerWidth,
} from '../utils'
import axios from 'axios'
import { languages } from 'src/model/languages'
import { updateBadgeReward } from '../../simple-box/badge-list'

// react-color only gives a solid hex; keep the alpha from the opacity slider
// by emitting rgba() whenever the color is not fully opaque.
const toCssColor = color => {
  const rgb = color?.rgb
  if (!rgb || rgb.a === undefined || rgb.a === null || rgb.a >= 1) {
    return color?.hex
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${Number(rgb.a.toFixed(2))})`
}

// Initial game schema for Pixel Quest
const INITIAL_GAME_SCHEMA = {
  title: 'The Path of the Product Bridge',
  mapGrid: {
    _hide: true,
    _value: [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 3, 3, 3, 1, 1, 1, 1, 1, 1,
      1, 1, 1, 3, 1, 1, 1, 1, 1, 1,
      1, 1, 1, 3, 3, 3, 3, 1, 1, 1,
      1, 1, 1, 1, 1, 1, 3, 1, 1, 1,
      1, 1, 3, 3, 3, 3, 3, 1, 1, 1,
      1, 1, 3, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 3, 3, 3, 3, 1, 1, 1, 1,
      1, 1, 1, 1, 1, 3, 3, 9, 1, 1,
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
    ],
  },
  startPosition: {
    _hide: true,
    _value: { x: 1, y: 1 },
  },
  config: {
    language: 'en',
    pathColor: '#374151',
    wallColor: '#111827',
    bgColor: '#020617',
    titleColor: '#facc15',
    exitText: 'EXIT',
    instructionText: 'ANSWER ALL MASTERS CORRECTLY TO UNLOCK THE EXIT',
    victoryText: 'QUEST COMPLETE!',
    gameOverText: 'GAME OVER',
    tryAgainText: 'TRY AGAIN',
  },
  npcs: [
    {
      id: 1,
      name: 'The Architect',
      spriteColor: '#3b82f6',
      position: {
        x: 3,
        y: 1,
      },
      question: 'Product Management acts as a bridge between which two historically misaligned groups?',
      options: {
        _type: 'radio',
        _options: [
          'Investors and Competitors',
          'Engineers and Business Leaders',
          'Project Managers and Designers',
          'HR and Marketing',
        ],
        _value: 'Engineers and Business Leaders',
      },
    },
    {
      id: 2,
      name: 'The Intersection Sage',
      spriteColor: '#ef4444',
      position: {
        x: 5,
        y: 3,
      },
      question: "According to the Intersection Model, what happens if you ignore the 'Technology' domain?",
      options: {
        _type: 'radio',
        _options: [
          'The company goes bankrupt',
          'Nobody uses the product',
          'The product cannot be built',
          'The product has no value proposition',
        ],
        _value: 'The product cannot be built',
      },
    },
    {
      id: 3,
      name: 'The Lean Strategist',
      spriteColor: '#10b981',
      position: {
        x: 4,
        y: 5,
      },
      question: 'What is the primary purpose of a Minimum Viable Product (MVP)?',
      options: {
        _type: 'radio',
        _options: [
          'To release a half-baked product quickly',
          'To save money on development costs',
          'To collect maximum validated learning with least effort',
          'To provide all features to early adopters',
        ],
        _value: 'To collect maximum validated learning with least effort',
      },
    },
    {
      id: 4,
      name: 'The Visionary Guide',
      spriteColor: '#f59e0b',
      position: {
        x: 4,
        y: 7,
      },
      question: 'Which of these is a key question asked by a Product Manager rather than a Project Manager?',
      options: {
        _type: 'radio',
        _options: [
          'How do we get this done by Friday?',
          'Should we build this?',
          'How do we allocate these resources?',
          'Is the budget adherence correct?',
        ],
        _value: 'Should we build this?',
      },
    },
  ],
}

// Initial crossword schema
const INITIAL_CROSSWORD_SCHEMA = {
  config: {
    title: 'Crossword Puzzle',
    bgColor: '#EFE8D8',
    primaryColor: '#3b82f6',
    textColor: '#ffffff',
    successText: 'Puzzle Completed!',
    failText: 'Try Again',
    language: 'en',
  },
  words: [
    {
      word: 'REACT',
      clue: 'A JavaScript library for building user interfaces',
    },
    {
      word: 'JAVASCRIPT',
      clue: 'The programming language of the Web',
    },
  ],
}

// Initial jumble schema - matches API format
const INITIAL_JUMBLE_SCHEMA = {
  config: {
    language: 'en',
    primaryColor: '#015ffb',
    accentColor: '#10b981',
    bgColor: '#f8fafc',
    showAnswer: true
  },
  words: [
    {
      word: 'HELLO',
      scrambled: 'OLLEH',
      definition: 'A common greeting used when meeting someone'
    },
    {
      word: 'WORLD',
      scrambled: 'DLROW',
      definition: 'The earth and all the people on it'
    }
  ]
}

// Initial riddle schema - matches JsonFormEngine format
const INITIAL_RIDDLE_SCHEMA = {
  config: {
    questTitle: 'Mystery Quest',
    language: 'en',
    primaryColor: '#4f46e5',
    bgColor: '#ffffff',
    cardBackground: '#ffffff',
    textMain: '#0f172a',
    textSecondary: '#64748b',
    accent: '#6366f1',
    success: '#059669',
  },
  riddles: [
    {
      answer: 'Piano',
      hints: [
        'I have keys but no locks',
        'I have space but no room',
        'You can enter but cannot go inside',
        'What am I?'
      ]
    }
  ]
}

// Initial wordle schema - matches JsonFormEngine format
const INITIAL_WORDLE_SCHEMA = {
  config: {
    questTitle: 'Word Quest',
    language: 'en',
    primaryColor: '#4f46e5',
    bgColor: '#ffffff',
    cardBackground: '#ffffff',
    textMain: '#0f172a',
    textSecondary: '#64748b',
    accent: '#6366f1',
    success: '#059669',
  },
  words: [
    {
      word: 'HELLO',
      hint: 'A common greeting'
    }
  ]
}

// Initial word search schema - matches API format
const INITIAL_WORD_SEARCH_SCHEMA = {
  config: {
    language: 'en',
    bgColor: '#ffffff',
    primaryColor: '#0ea5e9',
    accentColor: '#10b981',
    gridBackgroundColor: '#f0f9ff',
    textColor: '#1e293b',
    cardBackgroundColor: '#ffffff',
    wordItemBackgroundColor: '#f8fafc'
  },
  words: [
    'HELLO',
    'WORLD',
    'SEARCH',
    'PUZZLE'
  ]
}

// 🔍 CKEditor node yapısına göre düzeltilmiş fonksiyon
const findChildByCondition = (node, condition, maxDepth = 5) => {
  if (!node || maxDepth === 0) return null

  // Kendisi koşulu sağlıyor mu?
  if (condition(node)) return node

  // Çocuklarında ara (_children array'i)
  const children = node._children || []
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    const found = findChildByCondition(child, condition, maxDepth - 1)
    if (found) return found
  }

  return null
}

// 🎯 Class name'e göre child bulma (CKEditor için)
const findChildByClassName = (node, className, maxDepth = 5) => {
  return findChildByCondition(
    node,
    n => n?._classes?.has(className), // Set.has() kullan
    maxDepth,
  )
}

// 🔎 Attribute'a göre bulma
const findChildByAttribute = (node, attrName, attrValue, maxDepth = 5) => {
  return findChildByCondition(
    node,
    n => n?._attrs?.get(attrName) === attrValue, // Map.get() kullan
    maxDepth,
  )
}

// 🎪 Birden fazla koşul
const findChildByMultipleConditions = (node, conditions, maxDepth = 5) => {
  return findChildByCondition(
    node,
    n => {
      return conditions.every(cond => cond(n))
    },
    maxDepth,
  )
}

const BlockOptionsModal = props => {
  const { classes, editor, selection } = props
  const [background, setBackground] = useState('')
  const [sizing, setSizing] = useState('')
  const [buttonWidth, setButtonWidth] = useState('')
  const [imageURL, setImageURL] = useState('')
  const [size, setSize] = useState('SCALE_TO_FILL')
  const [alignment, setAlignment] = useState('center')
  const [textColor, setTextColor] = useState()
  const [columnCount, setColumnCount] = useState('3')
  const [opacityValue, setOpacityValue] = useState('0')
  const [overlayColor, setOverlayColor] = useState('#fff')
  const [pickerVisibles, setPickerVisibles] = useState({
    fontColor: false,
    backgroundColor: false,
    cardBackgroundColor: false,
    buttonColor: false,
  })
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null)
  const [pageReady, setPageReady] = useState(true)
  const [imagePosition, setImagePosition] = useState('')
  const [isChecked, setIsChecked] = useState(true)
  const [priceTagActive, setPriceTagActive] = useState(false)

  // Continue learning block states
  const [clTitle, setClTitle] = useState('Pick up where you left off')
  const [clLinkLabel, setClLinkLabel] = useState('Your learning')
  const [layoutType, setLayoutType] = useState('medium')

  // Community Block states
  const [moderation, setModeration] = useState('aiAuto')
  const [visitorPermissions, setVisitorPermissions] = useState('write')
  const [communityId, setCommunityId] = useState(null)
  const [communityLanguage, setCommunityLanguage] = useState('en')

  // Pixel Quest states
  const [pixelQuestConfig, setPixelQuestConfig] = useState(null)
  const [isUpdatingFromColorPicker, setIsUpdatingFromColorPicker] = useState(false)

  // Crossword Puzzle states
  const [crosswordConfig, setCrosswordConfig] = useState(null)
  const [isUpdatingCrosswordFromPicker, setIsUpdatingCrosswordFromPicker] = useState(false)

  // Riddle states
  const [riddleConfig, setRiddleConfig] = useState(null)
  const [isUpdatingRiddleFromPicker, setIsUpdatingRiddleFromPicker] = useState(false)

  // Wordle states
  const [wordleConfig, setWordleConfig] = useState(null)
  const [isUpdatingWordleFromPicker, setIsUpdatingWordleFromPicker] = useState(false)

  // Jumble states
  const [jumbleConfig, setJumbleConfig] = useState(null)
  const [isUpdatingJumbleFromPicker, setIsUpdatingJumbleFromPicker] = useState(false)

  // Word Search states
  const [wordSearchConfig, setWordSearchConfig] = useState(null)
  const [isUpdatingWordSearchFromPicker, setIsUpdatingWordSearchFromPicker] = useState(false)

  // Badge block states
  const [completionRewardBgColor, setCompletionRewardBgColor] = useState('linear-gradient(90deg, #004483 0%, #001C36 100%)')
  const [startText, setStartText] = useState('Start')
  const [resumeText, setResumeText] = useState('Resume')

  const rewardThemeColors = [
    'linear-gradient(90deg, #004483 0%, #001C36 100%)',
    'linear-gradient(90deg, #855EEC 0%, #D7559A 100%)',
    'linear-gradient(90deg, #4E81ED 0%, #55B5A7 100%)',
    'linear-gradient(90deg, #54B585 0%, #35A291 100%)',
    'linear-gradient(90deg, #8E5FF3 0%, #561FCD 100%)',
    'linear-gradient(90deg, #EC8200 0%, #E4352E 100%)',
    'linear-gradient(90deg, #3B82F6 0%, #64748B 100%)',
  ]

  // Redux selectors for default language
  const course = useSelector(state => state.course?.course)
  const collection = useSelector(state => state.collection?.collection)
  const customDomain = useSelector(state => state.domain?.list)
  const customDomainList = customDomain && customDomain.results

  const onHandleChange = () => {
    setIsChecked(!isChecked)
    editor.editing.view.focus()
    handleImageOverlay(editor, selection, isChecked, imageURL)
  }

  const onHandleAlignment = (event, newAlignment) => {
    if (newAlignment !== null && alignment !== newAlignment) {
      setAlignment(newAlignment)
      editor.editing.view.focus()
      handleAlignment(editor, selection, newAlignment)
    }
  }

  const onHandleBackground = (event, newBackground) => {
    if (newBackground === 'image') {
      setTextColor('#ffffff')
    } else {
      setImageURL('')
      setBackground('')
      setImagePosition('center')
      setSize('SCALE_TO_FILL')
    }
    if (background !== newBackground) {
      setBackground(newBackground)
      if (newBackground !== null) {
        editor.editing.view.focus()
        handleBackground(editor, selection, newBackground)
      }
    }
  }

  useEffect(() => {
    const selectedElement = selection

    // ✅ Dinamik arama - button-wrapper için
    const child = findChildByClassName(selection, 'button-wrapper')

    if (child) {
      setButtonWidth(child._attrs?.get('data-sizing'))
      setAlignment(child._attrs?.get('data-alignment') || 'center')
    }

    // ✅ Dinamik arama - dynamic-list için
    const dynamicListChild = findChildByClassName(selection, 'dynamic-list')

    if (dynamicListChild) {
      const dataType = dynamicListChild._attrs?.get('data-type')
      if (
        dataType === 'courseList1' ||
        dataType === 'collectionList' ||
        dataType === 'catalog' ||
        dataType === 'continueLearning'
      ) {
        const template = dynamicListChild._attrs?.get('data-grid') || '3'
        setColumnCount(template)

        // Price tag active kontrolü
        const priceTag =
          dynamicListChild._attrs?.get('data-price-tag-active') === 'true'
        setPriceTagActive(priceTag)
      }

      if (dataType === 'continueLearning') {
        setClTitle(
          dynamicListChild._attrs?.get('data-cl-title') ||
            'Pick up where you left off',
        )
        setClLinkLabel(
          dynamicListChild._attrs?.get('data-cl-link-label') || 'Your learning',
        )
      }

      if (dataType === 'badge') {
        setCompletionRewardBgColor(
          dynamicListChild._attrs?.get('data-completion-reward-bg-color') || 'linear-gradient(90deg, #004483 0%, #001C36 100%)',
        )
        setStartText(
          dynamicListChild._attrs?.get('data-start') || 'Start',
        )
        setResumeText(
          dynamicListChild._attrs?.get('data-resume') || 'Resume',
        )
      }
    }

    setTextColor(selectedElement._attrs?.get('background-color') || '#ffffff')
    setBackground(selectedElement._attrs?.get('background-type') || 'color')
    setImageURL(selectedElement._attrs?.get('background-image'))
    setImagePosition(
      selectedElement._attrs?.get('background-position') || 'center',
    )
    setSize(selectedElement._attrs?.get('size-type') || 'SCALE_TO_FILL')
    setIsChecked(selectedElement._attrs?.get('use-image-overlay') === 'true')

    const opacityValue = selectedElement._attrs?.get('opacity') || '0'
    setOpacityValue(parseFloat(opacityValue) * 100)

    setOverlayColor(selectedElement._attrs?.get('overlay-color') || '#fff')
    setSizing('fit')
    setLayoutType(selectedElement._attrs?.get('layout-type') || 'medium')

    // Load community block attributes
    const communityChild = findChildByClassName(
      selection,
      'community-block-widget-container',
    )
    if (communityChild) {
      setModeration(communityChild._attrs?.get('data-moderation') || 'aiAuto')
      setVisitorPermissions(
        communityChild._attrs?.get('data-visitor-permissions') || 'write',
      )
      setCommunityId(communityChild._attrs?.get('data-community-id') || null)

      // Get community language - default to course/collection language or 'en'
      const savedLanguage = communityChild._attrs?.get('data-community-language')
      if (savedLanguage) {
        setCommunityLanguage(savedLanguage)
      } else {
        // Default: course language > collection language > 'en'
        const defaultLang = course?.language || collection?.language || 'en'
        setCommunityLanguage(defaultLang)
      }
    }

    // Load pixel quest attributes
    const pixelQuestChild = findChildByClassName(
      selection,
      'pixel-quest-widget-container',
    )
    if (pixelQuestChild) {
      const gameConfigStr = pixelQuestChild._attrs?.get('data-game-config')

      if (gameConfigStr) {
        try {
          const parsedConfig = JSON.parse(gameConfigStr)
          // Merge with INITIAL_GAME_SCHEMA so all config fields (e.g. text fields added
          // after a block was originally created) are always present in the form.
          const mergedConfig = {
            ...INITIAL_GAME_SCHEMA,
            ...parsedConfig,
            config: {
              ...INITIAL_GAME_SCHEMA.config,
              ...parsedConfig.config,
            },
          }
          // Only set if different from current config to prevent re-renders
          if (JSON.stringify(mergedConfig) !== JSON.stringify(pixelQuestConfig)) {
            setPixelQuestConfig(mergedConfig)
          }
        } catch (e) {
          if (pixelQuestConfig !== null) {
            setPixelQuestConfig(null)
          }
        }
      } else if (!pixelQuestConfig) {
        // No config yet, create default only if we don't have one
        setPixelQuestConfig({ ...INITIAL_GAME_SCHEMA })
      }
    }

    // Load crossword puzzle attributes
    const crosswordChild = findChildByClassName(
      selection,
      'crossword-puzzle-widget-container',
    )
    if (crosswordChild) {
      const puzzleConfigStr = crosswordChild._attrs?.get('data-puzzle-config')

      if (puzzleConfigStr) {
        try {
          const parsedConfig = JSON.parse(puzzleConfigStr)
          // Only set if different from current config to prevent re-renders
          if (JSON.stringify(parsedConfig) !== JSON.stringify(crosswordConfig)) {
            setCrosswordConfig(parsedConfig)
          }
        } catch (e) {
          if (crosswordConfig !== null) {
            setCrosswordConfig(null)
          }
        }
      } else if (!crosswordConfig) {
        // No config yet, use null (will be populated from API)
        setCrosswordConfig(null)
      }
    }

    // Load riddle attributes
    const riddleChild = findChildByClassName(
      selection,
      'riddle-widget-container',
    )
    if (riddleChild) {
      const gameConfigStr = riddleChild._attrs?.get('data-game-config')

      if (gameConfigStr) {
        try {
          const parsedConfig = JSON.parse(gameConfigStr)
          // Only set if different from current config to prevent re-renders
          if (JSON.stringify(parsedConfig) !== JSON.stringify(riddleConfig)) {
            setRiddleConfig(parsedConfig)
          }
        } catch (e) {
          if (riddleConfig !== null) {
            setRiddleConfig(null)
          }
        }
      } else if (!riddleConfig) {
        // No config yet, use null (will be populated from API)
        setRiddleConfig(null)
      }
    }

    // Load wordle attributes
    const wordleChild = findChildByClassName(
      selection,
      'wordle-widget-container',
    )
    if (wordleChild) {
      const gameConfigStr = wordleChild._attrs?.get('data-game-config')

      if (gameConfigStr) {
        try {
          const parsedConfig = JSON.parse(gameConfigStr)
          // Only set if different from current config to prevent re-renders
          if (JSON.stringify(parsedConfig) !== JSON.stringify(wordleConfig)) {
            setWordleConfig(parsedConfig)
          }
        } catch (e) {
          if (wordleConfig !== null) {
            setWordleConfig(null)
          }
        }
      } else if (!wordleConfig) {
        // No config yet, use null (will be populated from API)
        setWordleConfig(null)
      }
    }

    // Load jumble attributes
    const jumbleChild = findChildByClassName(
      selection,
      'jumble-widget-container',
    )
    if (jumbleChild) {
      const gameConfigStr = jumbleChild._attrs?.get('data-game-config')

      if (gameConfigStr) {
        try {
          const parsedConfig = JSON.parse(gameConfigStr)
          // Only set if different from current config to prevent re-renders
          if (JSON.stringify(parsedConfig) !== JSON.stringify(jumbleConfig)) {
            setJumbleConfig(parsedConfig)
          }
        } catch (e) {
          if (jumbleConfig !== null) {
            setJumbleConfig(null)
          }
        }
      } else if (!jumbleConfig) {
        // No config yet, use null (will be populated from API)
        setJumbleConfig(null)
      }
    }

    // Load word search attributes
    const wordSearchChild = findChildByClassName(
      selection,
      'word-search-widget-container',
    )
    if (wordSearchChild) {
      const gameConfigStr = wordSearchChild._attrs?.get('data-game-config')

      if (gameConfigStr) {
        try {
          const parsedConfig = JSON.parse(gameConfigStr)
          // Only set if different from current config to prevent re-renders
          if (JSON.stringify(parsedConfig) !== JSON.stringify(wordSearchConfig)) {
            setWordSearchConfig(parsedConfig)
          }
        } catch (e) {
          if (wordSearchConfig !== null) {
            setWordSearchConfig(null)
          }
        }
      } else if (!wordSearchConfig) {
        // No config yet, use null (will be populated from API)
        setWordSearchConfig(null)
      }
    }
  }, [selection, course, collection])

  const handleSliderChange = value => {
    setOpacityValue(value * 100)
    handleOverlayColor(editor, selection, overlayColor, value, imageURL, isChecked)
  }

  const onHandleContainerWidth = (event, newSizing) => {
    if (newSizing !== null) {
      setSizing(newSizing)
      handleContainerWidth(editor, selection, newSizing)
    }
  }

  const onHandleSize = e => {
    const selectedSize = e.target.value
    if (size !== selectedSize) {
      setSize(selectedSize)
      handleSize(editor, selection, selectedSize)
    }
  }

  // ✅ Dinamik arama ile child'ları bul
  const child = findChildByClassName(selection, 'button-wrapper')
  const isButtonWrapper = !!child

  const childDynamicList = findChildByClassName(selection, 'dynamic-list')
  const dataType = childDynamicList?._attrs?.get('data-type')
  const isDynamicList =
    childDynamicList &&
    (dataType === 'courseList1' ||
      dataType === 'collectionList' ||
      dataType === 'catalog' ||
      dataType === 'continueLearning')
  // Only collections carry a price, but the catalog and continue learning blocks show
  // collections too, so they get the toggle.
  const isPriceTagActive =
    childDynamicList &&
    (dataType === 'collectionList' ||
      dataType === 'catalog' ||
      dataType === 'continueLearning')
  const isCatalogBlock = childDynamicList && dataType === 'catalog'
  const isContinueLearningBlock =
    childDynamicList && dataType === 'continueLearning'
  const isBadgeBlock = childDynamicList && dataType === 'badge'

  // ✅ Community Block kontrolü
  const communityBlockChild = findChildByClassName(
    selection,
    'community-block-widget-container',
  )
  const isCommunityBlock = !!communityBlockChild

  // ✅ Pixel Quest kontrolü
  const pixelQuestChild = findChildByClassName(
    selection,
    'pixel-quest-widget-container',
  )
  const isPixelQuest = !!pixelQuestChild

  // ✅ Crossword Puzzle kontrolü
  const crosswordChild = findChildByClassName(
    selection,
    'crossword-puzzle-widget-container',
  )
  const isCrossword = !!crosswordChild

  // ✅ Riddle kontrolü
  const riddleChild = findChildByClassName(
    selection,
    'riddle-widget-container',
  )
  const isRiddle = !!riddleChild

  // ✅ Wordle kontrolü
  const wordleChild = findChildByClassName(
    selection,
    'wordle-widget-container',
  )
  const isWordle = !!wordleChild

  // ✅ Jumble kontrolü
  const jumbleChild = findChildByClassName(
    selection,
    'jumble-widget-container',
  )
  const isJumble = !!jumbleChild

  // ✅ Word Search kontrolü
  const wordSearchChild = findChildByClassName(
    selection,
    'word-search-widget-container',
  )
  const isWordSearch = !!wordSearchChild

  const onHandleCompletionRewardBgColor = gradient => {
    setCompletionRewardBgColor(gradient)

    if (childDynamicList) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          childDynamicList,
        )
        if (modelElement) {
          writer.setAttribute('completionRewardBgColor', gradient, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute(
                'data-completion-reward-bg-color',
                gradient,
                viewElement,
              )
            })
          }
        }
      })

      // Use getDomRoot to find the reward card in the editor DOM
      const domRoot = editor.editing.view.getDomRoot()
      if (domRoot) {
        const rewardCard = domRoot.querySelector('.badge-completion-reward-card')
        if (rewardCard) {
          rewardCard.style.background = gradient
          // Remove any inline background from text container
          const textContainer = rewardCard.querySelector('.badge-reward-text-container')
          if (textContainer) {
            textContainer.style.background = ''
          }
          const rewardSection = domRoot.querySelector('.badge-completion-reward')
          const toggle = rewardSection ? rewardSection.querySelector('.badge-reward-toggle') : null
          const isEnabled = toggle ? toggle.checked : true
          const collectionId = collection?.id
          if (collectionId) {
            // Save without inline background - bgColor comes from simple-box-wrapper attribute
            const cardClone = rewardCard.cloneNode(true)
            cardClone.style.background = ''
            updateBadgeReward(collectionId, cardClone.outerHTML, isEnabled)
          }
        }
      }
    }
  }

  const onHandleStartTextChange = event => {
    const newValue = event.target.value
    setStartText(newValue)

    if (childDynamicList) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          childDynamicList,
        )
        if (modelElement) {
          writer.setAttribute('startText', newValue, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-start', newValue, viewElement)
            })
          }
        }
      })

      // Update DOM immediately
      const domRoot = editor.editing.view.getDomRoot()
      if (domRoot) {
        domRoot.querySelectorAll('.badge-list-card-start-btn').forEach(btn => {
          btn.innerHTML = `${newValue}\n        <iconify-icon icon="mdi:chevron-right" width="20" height="20"></iconify-icon>`
        })
      }
    }
  }

  const onHandleResumeTextChange = event => {
    const newValue = event.target.value
    setResumeText(newValue)

    if (childDynamicList) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          childDynamicList,
        )
        if (modelElement) {
          writer.setAttribute('resumeText', newValue, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute(
                'data-resume',
                newValue,
                viewElement,
              )
            })
          }
        }
      })
    }
  }

  const onHandleColumnChange = (event, newValue) => {
    if (newValue !== null) {
      setColumnCount(newValue)

      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          childDynamicList,
        )
        if (modelElement) {
          writer.setAttribute('grid', newValue, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(writer => {
              writer.setAttribute('data-grid', newValue, viewElement)
            })
          }
        }
      })

      editor.editing.view.focus()
    }
  }

  const onHandlePriceTagChange = () => {
    const newValue = !priceTagActive
    setPriceTagActive(newValue)

    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(
        childDynamicList,
      )
      if (modelElement) {
        writer.setAttribute('priceTagActive', newValue, modelElement)

        const viewElement = editor.editing.mapper.toViewElement(modelElement)
        if (viewElement) {
          editor.editing.view.change(writer => {
            writer.setAttribute(
              'data-price-tag-active',
              newValue.toString(),
              viewElement,
            )
          })
        }
      }
    })

    editor.editing.view.focus()
  }

  /**
   * Continue learning settings all follow the same shape: write the model attribute so the
   * value survives a save, and mirror it onto the view so the widget re-renders right away.
   */
  const setDynamicListAttribute = (modelAttribute, viewAttribute, value) => {
    if (!childDynamicList) return

    editor.model.change(writer => {
      const modelElement = editor.editing.mapper.toModelElement(childDynamicList)
      if (!modelElement) return

      writer.setAttribute(modelAttribute, value, modelElement)

      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (viewElement) {
        editor.editing.view.change(viewWriter => {
          viewWriter.setAttribute(viewAttribute, String(value), viewElement)
        })
      }
    })
  }

  const onHandleClTitleChange = event => {
    const newValue = event.target.value
    setClTitle(newValue)
    setDynamicListAttribute('clTitle', 'data-cl-title', newValue)
  }

  const onHandleClLinkLabelChange = event => {
    const newValue = event.target.value
    setClLinkLabel(newValue)
    setDynamicListAttribute('clLinkLabel', 'data-cl-link-label', newValue)
  }

  const onHandleImageDelete = () => {
    setImageURL('')
    setBackground('')
    setImagePosition('center')
    setSize('SCALE_TO_FILL')
    handleImageDelete(editor, selection)
  }

  const handleImage = type => {
    track('media_gallery_in_editor_clicked')

    const onOk = item => {
      const itemUrl = item[0].url
      if (imageURL !== itemUrl) {
        setImageURL(itemUrl)
        if (selection) {
          editor.model.change(writer => {
            const modelElement = editor.editing.mapper.toModelElement(selection)
            if (modelElement) {
              if (modelElement.getAttribute('backgroundImage') !== itemUrl) {
                writer.setAttribute('backgroundImage', itemUrl, modelElement)
              }
              if (modelElement.getAttribute('backgroundColor') !== '') {
                writer.setAttribute('backgroundColor', '', modelElement)
              }
              if (modelElement.getAttribute('backgroundSize') !== size) {
                writer.setAttribute('backgroundSize', size, modelElement)
              }
              if (
                modelElement.getAttribute('backgroundPosition') !==
                imagePosition
              ) {
                writer.setAttribute(
                  'backgroundPosition',
                  imagePosition,
                  modelElement,
                )
              }
              const viewElement = editor.editing.mapper.toViewElement(
                modelElement,
              )
              if (viewElement) {
                editor.editing.view.change(writer => {
                  writer.setStyle(
                    'background-image',
                    `url(${itemUrl})`,
                    viewElement,
                  )
                  writer.setStyle(
                    'background-position',
                    `${imagePosition}`,
                    viewElement,
                  )
                  const SizeType = {
                    SCALE_TO_FILL: { name: 'Scale to fill', class: 'cover' },
                    ORIGINAL_SIZE: { name: 'Original size', class: 'auto' },
                    TILE: { name: 'Tile', class: 'contain' },
                  }
                  writer.setStyle(
                    'background-size',
                    `${SizeType[size].class}`,
                    viewElement,
                  )
                })
              }
            }
          })
        }
      }
      dispatch(messageBoxHideAction('message-box-1'))
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: `
            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <Icon icon="ic:round-add-a-photo" color="#00AB55" width="24" height="24" />
              <Typography style="margin-left: 8; color: #002E47; fontWeight: 600; fontSize: 16;">
                Media Gallery
              </Typography>
            </div>`,
          component: (
            <ContainerMediaGalery onOk={onOk} setImageURL={setImageURL} />
          ),
          variant: MessageBoxVariant.DRAGGABLE,
          maxHeight: '640px',
        }),
      ),
    )
  }

  const onHandleOverlayColor = event => {
    setOverlayColor(event)
    // `opacityValue` is kept in percent (0-100) for the slider, but the overlay
    // expects a 0-1 alpha.
    handleOverlayColor(
      editor,
      selection,
      event,
      Number(opacityValue) / 100,
      imageURL,
      isChecked,
    )
  }

  const onChangePosition = event => {
    setImagePosition(event)
    handlePosition(editor, selection, event)
  }

  const onHandleColor = event => {
    setTextColor(event)
    handleColor(editor, selection, event)

    // If Pixel Quest is selected, also update the config.bgColor
    if (isPixelQuest && pixelQuestConfig) {
      setIsUpdatingFromColorPicker(true)
      const updatedConfig = {
        ...pixelQuestConfig,
        config: {
          ...pixelQuestConfig.config,
          bgColor: event,
        },
      }
      // Directly update without triggering onChange loop
      setPixelQuestConfig(updatedConfig)

      // Update the gameConfig attribute on pixelQuest element
      if (pixelQuestChild) {
        const configStr = JSON.stringify(updatedConfig)
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(pixelQuestChild)
          if (modelElement) {
            writer.setAttribute('gameConfig', configStr, modelElement)

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (viewElement) {
              editor.editing.view.change(viewWriter => {
                viewWriter.setAttribute('data-game-config', configStr, viewElement)
              })
            }
          }
        })
      }
      // Reset flag after a short delay
      setTimeout(() => setIsUpdatingFromColorPicker(false), 100)
    }

    // If Crossword is selected, also update the config.bgColor
    if (isCrossword && crosswordConfig) {
      setIsUpdatingCrosswordFromPicker(true)
      const updatedConfig = {
        ...crosswordConfig,
        config: {
          ...crosswordConfig.config,
          bgColor: event,
        },
      }
      // Directly update without triggering onChange loop
      setCrosswordConfig(updatedConfig)

      // Update the puzzleConfig attribute on crosswordPuzzle element
      if (crosswordChild) {
        const configStr = JSON.stringify(updatedConfig)
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(crosswordChild)
          if (modelElement) {
            writer.setAttribute('puzzleConfig', configStr, modelElement)

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (viewElement) {
              editor.editing.view.change(viewWriter => {
                viewWriter.setAttribute('data-puzzle-config', configStr, viewElement)
              })
            }
          }
        })
      }
      // Reset flag after a short delay
      setTimeout(() => setIsUpdatingCrosswordFromPicker(false), 100)
    }

    // If Riddle is selected, also update the colors.background
    if (isRiddle && riddleConfig) {
      setIsUpdatingRiddleFromPicker(true)
      const updatedConfig = {
        ...riddleConfig,
        colors: {
          ...riddleConfig.colors,
          background: event,
        },
      }
      // Directly update without triggering onChange loop
      setRiddleConfig(updatedConfig)

      // Update the gameConfig attribute on riddle element
      if (riddleChild) {
        const configStr = JSON.stringify(updatedConfig)
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(riddleChild)
          if (modelElement) {
            writer.setAttribute('gameConfig', configStr, modelElement)

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (viewElement) {
              editor.editing.view.change(viewWriter => {
                viewWriter.setAttribute('data-game-config', configStr, viewElement)
              })
            }
          }
        })
      }
      // Reset flag after a short delay
      setTimeout(() => setIsUpdatingRiddleFromPicker(false), 100)
    }

    // If Wordle is selected, also update the colors.background
    if (isWordle && wordleConfig) {
      setIsUpdatingWordleFromPicker(true)
      const updatedConfig = {
        ...wordleConfig,
        colors: {
          ...wordleConfig.colors,
          background: event,
        },
      }
      // Directly update without triggering onChange loop
      setWordleConfig(updatedConfig)

      // Update the gameConfig attribute on wordle element
      if (wordleChild) {
        const configStr = JSON.stringify(updatedConfig)
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(wordleChild)
          if (modelElement) {
            writer.setAttribute('gameConfig', configStr, modelElement)

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (viewElement) {
              editor.editing.view.change(viewWriter => {
                viewWriter.setAttribute('data-game-config', configStr, viewElement)
              })
            }
          }
        })
      }
      // Reset flag after a short delay
      setTimeout(() => setIsUpdatingWordleFromPicker(false), 100)
    }

    // If Jumble is selected, also update the colors.background
    if (isJumble && jumbleConfig) {
      setIsUpdatingJumbleFromPicker(true)
      const updatedConfig = {
        ...jumbleConfig,
        colors: {
          ...jumbleConfig.colors,
          background: event,
        },
      }
      // Directly update without triggering onChange loop
      setJumbleConfig(updatedConfig)

      // Update the gameConfig attribute on jumble element
      if (jumbleChild) {
        const configStr = JSON.stringify(updatedConfig)
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(jumbleChild)
          if (modelElement) {
            writer.setAttribute('gameConfig', configStr, modelElement)

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (viewElement) {
              editor.editing.view.change(viewWriter => {
                viewWriter.setAttribute('data-game-config', configStr, viewElement)
              })
            }
          }
        })
      }
      // Reset flag after a short delay
      setTimeout(() => setIsUpdatingJumbleFromPicker(false), 100)
    }

    // If Word Search is selected, also update the colors.background
    if (isWordSearch && wordSearchConfig) {
      setIsUpdatingWordSearchFromPicker(true)
      const updatedConfig = {
        ...wordSearchConfig,
        colors: {
          ...wordSearchConfig.colors,
          background: event,
        },
      }
      // Directly update without triggering onChange loop
      setWordSearchConfig(updatedConfig)

      // Update the gameConfig attribute on wordSearch element
      if (wordSearchChild) {
        const configStr = JSON.stringify(updatedConfig)
        editor.model.change(writer => {
          const modelElement = editor.editing.mapper.toModelElement(wordSearchChild)
          if (modelElement) {
            writer.setAttribute('gameConfig', configStr, modelElement)

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (viewElement) {
              editor.editing.view.change(viewWriter => {
                viewWriter.setAttribute('data-game-config', configStr, viewElement)
              })
            }
          }
        })
      }
      // Reset flag after a short delay
      setTimeout(() => setIsUpdatingWordSearchFromPicker(false), 100)
    }
  }

  const onHandleButtonWidth = (event, newSizing) => {
    if (newSizing !== null) {
      setButtonWidth(newSizing)
      editor.editing.view.focus()
      handleButtonWidth(editor, selection, newSizing)
    }
  }

  const onHandleLayoutType = (event, newLayoutType) => {
    if (newLayoutType !== null && layoutType !== newLayoutType) {
      setLayoutType(newLayoutType)

      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('layoutType', newLayoutType, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('layout-type', newLayoutType, viewElement)
            })
          }
        }
      })

      editor.editing.view.focus()
    }
  }

  // Helper function to convert permissions to API format
  const getPermissionsForAPI = permissionType => {
    const permissionsMap = {
      reader: { can_read: true, can_comment: false, can_post: false },
      commenter: { can_read: true, can_comment: true, can_post: false },
      write: { can_read: true, can_comment: true, can_post: true },
    }
    return permissionsMap[permissionType] || permissionsMap.write
  }

  // Helper function to convert moderation value for API
  const getModerationForAPI = moderationValue => {
    const moderationMap = {
      none: 'none',
      aiAuto: 'aiService',
      manually: 'manual',
    }
    return moderationMap[moderationValue] || 'aiService'
  }

  // Build shareLink from course/collection data and custom domains
  const getShareLink = () => {
    const urlPath = window.location.pathname
    const isMiniCourse = urlPath.includes('/mini-course/')
    const isCollection = urlPath.includes('/collection/')

    let courseId = null
    let collectionId = null

    if (isMiniCourse) {
      courseId = window.CONSTANTS?.COURSE_ID || course?.id
    } else if (isCollection) {
      collectionId = collection?.id
    }

    const courseCode = course?.code || collection?.code || window.CONSTANTS?.COURSE_CODE || window.CONSTANTS?.CODE

    let domainName = 'share.minicoursegenerator.com'

    if (customDomainList) {
      if (isCollection && collectionId) {
        const collectionDomain = customDomainList.filter(
          item =>
            item.defaultCollectionDto != null &&
            item.defaultCollectionDto.id == collectionId &&
            item.verificationStatus == 1,
        )
        if (collectionDomain.length > 0) {
          domainName = collectionDomain[0].domain
        }
      } else if (isMiniCourse && courseId) {
        const courseDomain = customDomainList.filter(
          item =>
            item.defaultCourseDto != null &&
            item.defaultCourseDto.id == courseId &&
            item.verificationStatus == 1,
        )
        if (courseDomain.length > 0) {
          domainName = courseDomain[0].domain
        }
      }
    }

    if (domainName !== 'share.minicoursegenerator.com') {
      return `https://${domainName}`
    } else if (isCollection && collection) {
      const formattedName = collection.name
        .replaceAll(' ', '-')
        .replaceAll('?', '')
        .replaceAll('&', '')
        .replaceAll('#', '')
        .replaceAll('%', '')
      return `https://${domainName}/category/${formattedName}-1/${collection.id}`
    } else if (isMiniCourse && courseCode) {
      return `https://${domainName}/${courseCode}`
    }

    return null
  }

  // Update community settings via API
  const updateCommunitySettings = async (moderationValue, permissionsValue) => {
    if (!communityId) {
      console.error('Community ID not found')
      return
    }

    try {
      const apiUrl = process.env.REACT_APP_COMMUNITY_API_URL || 'https://community-2zj0.onrender.com/api'
      const baseURL = apiUrl.replace('/api', '')

      const axiosInstance = axios.create({
        baseURL: baseURL,
        withCredentials: false,
      })

      const data = {
        settings: {
          moderation: getModerationForAPI(moderationValue),
          permissions: getPermissionsForAPI(permissionsValue),
        },
        user_info: {
          userId: window.CONSTANTS?.ID,
          email: window.CONSTANTS?.EMAIL,
          name: window.CONSTANTS?.NAME,
          surname: window.CONSTANTS?.SUR_NAME,
          companyName: window.CONSTANTS?.COMPANY_NAME,
          url: [
            {
              adminUrl: window.location.href,
              shareLink: getShareLink(),
            },
          ],
        },
      }

      await axiosInstance.put(`/api/communities/${communityId}`, data)
    } catch (error) {
      console.error('Failed to update community settings:', error)
    }
  }

  // Community Block handlers
  const onHandleModerationChange = event => {
    const newValue = event.target.value
    setModeration(newValue)

    if (communityBlockChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          communityBlockChild,
        )
        if (modelElement) {
          writer.setAttribute('moderation', newValue, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(writer => {
              writer.setAttribute('data-moderation', newValue, viewElement)
            })
          }
        }
      })

      // Update via API
      updateCommunitySettings(newValue, visitorPermissions)
    }

    editor.editing.view.focus()
  }

  const onHandleVisitorPermissionsChange = event => {
    const newValue = event.target.value
    setVisitorPermissions(newValue)

    if (communityBlockChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          communityBlockChild,
        )
        if (modelElement) {
          writer.setAttribute('visitorPermissions', newValue, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(writer => {
              writer.setAttribute(
                'data-visitor-permissions',
                newValue,
                viewElement,
              )
            })
          }
        }
      })

      // Update via API
      updateCommunitySettings(moderation, newValue)
    }

    editor.editing.view.focus()
  }

  const onHandleCommunityLanguageChange = event => {
    const newValue = event.target.value
    setCommunityLanguage(newValue)

    if (communityBlockChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          communityBlockChild,
        )
        if (modelElement) {
          writer.setAttribute('communityLanguage', newValue, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(writer => {
              writer.setAttribute(
                'data-community-language',
                newValue,
                viewElement,
              )
            })
          }
        }
      })
    }

    editor.editing.view.focus()
  }

  // Pixel Quest config change handler
  const onHandlePixelQuestConfigChange = newConfig => {
    // Prevent infinite loop: only update if config actually changed
    const newConfigStr = JSON.stringify(newConfig)
    const oldConfigStr = JSON.stringify(pixelQuestConfig)
    if (newConfigStr === oldConfigStr) {
      return
    }

    setPixelQuestConfig(newConfig)

    // Sync bgColor to simpleBoxWrapper (only if not coming from color picker)
    const newBgColor = newConfig?.config?.bgColor
    if (newBgColor && newBgColor !== textColor && !isUpdatingFromColorPicker) {
      setTextColor(newBgColor)
      // Update simpleBoxWrapper background color
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('backgroundColor', newBgColor, modelElement)
          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('background-color', newBgColor, viewElement)
              viewWriter.setStyle('background-color', newBgColor, viewElement)
            })
          }
        }
      })
    }

    if (pixelQuestChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(
          pixelQuestChild,
        )
        if (modelElement) {
          writer.setAttribute('gameConfig', newConfigStr, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-game-config', newConfigStr, viewElement)
            })
          }
        }
      })
    }
    // editor.editing.view.focus() kaldırıldı - input focus kaybetmesin
  }

  // Crossword Puzzle config change handler
  const onHandleCrosswordConfigChange = newConfig => {
    // Prevent infinite loop: only update if config actually changed
    const newConfigStr = JSON.stringify(newConfig)
    const oldConfigStr = JSON.stringify(crosswordConfig)
    if (newConfigStr === oldConfigStr) {
      return
    }

    setCrosswordConfig(newConfig)

    // Sync bgColor to simpleBoxWrapper (if config has bgColor)
    const newBgColor = newConfig?.config?.bgColor

    if (newBgColor && newBgColor !== textColor && !isUpdatingCrosswordFromPicker) {
      setTextColor(newBgColor)
      // Update simpleBoxWrapper background color
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('backgroundColor', newBgColor, modelElement)
          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('background-color', newBgColor, viewElement)
              viewWriter.setStyle('background-color', newBgColor, viewElement)
            })
          }
        }
      })
    }

    // Update the puzzleConfig attribute on crosswordPuzzle element
    if (crosswordChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(crosswordChild)
        if (modelElement) {
          writer.setAttribute('puzzleConfig', newConfigStr, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-puzzle-config', newConfigStr, viewElement)
            })
          }
        }
      })
    }
  }

  // Riddle config change handler
  const onHandleRiddleConfigChange = newConfig => {
    // Prevent infinite loop: only update if config actually changed
    const newConfigStr = JSON.stringify(newConfig)
    const oldConfigStr = JSON.stringify(riddleConfig)
    if (newConfigStr === oldConfigStr) {
      return
    }

    setRiddleConfig(newConfig)

    // Sync bgColor to simpleBoxWrapper (only if not coming from color picker)
    // Support both formats: config.bgColor and colors.background
    const newBgColor = newConfig?.config?.bgColor || newConfig?.colors?.background
    if (newBgColor && newBgColor !== textColor && !isUpdatingRiddleFromPicker) {
      setTextColor(newBgColor)
      // Update simpleBoxWrapper background color
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('backgroundColor', newBgColor, modelElement)
          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('background-color', newBgColor, viewElement)
              viewWriter.setStyle('background-color', newBgColor, viewElement)
            })
          }
        }
      })
    }

    if (riddleChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(riddleChild)
        if (modelElement) {
          writer.setAttribute('gameConfig', newConfigStr, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-game-config', newConfigStr, viewElement)
            })
          }
        }
      })
    }
  }

  // Wordle config change handler
  const onHandleWordleConfigChange = newConfig => {
    // Prevent infinite loop: only update if config actually changed
    const newConfigStr = JSON.stringify(newConfig)
    const oldConfigStr = JSON.stringify(wordleConfig)
    if (newConfigStr === oldConfigStr) {
      return
    }

    setWordleConfig(newConfig)

    // Sync bgColor to simpleBoxWrapper (only if not coming from color picker)
    // Support both formats: config.bgColor and colors.background
    const newBgColor = newConfig?.config?.bgColor || newConfig?.colors?.background
    if (newBgColor && newBgColor !== textColor && !isUpdatingWordleFromPicker) {
      setTextColor(newBgColor)
      // Update simpleBoxWrapper background color
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('backgroundColor', newBgColor, modelElement)
          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('background-color', newBgColor, viewElement)
              viewWriter.setStyle('background-color', newBgColor, viewElement)
            })
          }
        }
      })
    }

    if (wordleChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(wordleChild)
        if (modelElement) {
          writer.setAttribute('gameConfig', newConfigStr, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-game-config', newConfigStr, viewElement)
            })
          }
        }
      })
    }
  }

  // Jumble config change handler
  const onHandleJumbleConfigChange = newConfig => {
    // Prevent infinite loop: only update if config actually changed
    const newConfigStr = JSON.stringify(newConfig)
    const oldConfigStr = JSON.stringify(jumbleConfig)
    if (newConfigStr === oldConfigStr) {
      return
    }

    setJumbleConfig(newConfig)

    // Sync bgColor to simpleBoxWrapper (only if not coming from color picker)
    // Support both formats: config.bgColor and colors.background
    const newBgColor = newConfig?.config?.bgColor || newConfig?.colors?.background
    if (newBgColor && newBgColor !== textColor && !isUpdatingJumbleFromPicker) {
      setTextColor(newBgColor)
      // Update simpleBoxWrapper background color
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('backgroundColor', newBgColor, modelElement)
          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('background-color', newBgColor, viewElement)
              viewWriter.setStyle('background-color', newBgColor, viewElement)
            })
          }
        }
      })
    }

    if (jumbleChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(jumbleChild)
        if (modelElement) {
          writer.setAttribute('gameConfig', newConfigStr, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-game-config', newConfigStr, viewElement)
            })
          }
        }
      })
    }
  }

  // Word Search config change handler
  const onHandleWordSearchConfigChange = newConfig => {
    // Prevent infinite loop: only update if config actually changed
    const newConfigStr = JSON.stringify(newConfig)
    const oldConfigStr = JSON.stringify(wordSearchConfig)
    if (newConfigStr === oldConfigStr) {
      return
    }

    setWordSearchConfig(newConfig)

    // Sync bgColor to simpleBoxWrapper (only if not coming from color picker)
    // Support both formats: config.bgColor and colors.background
    const newBgColor = newConfig?.config?.bgColor || newConfig?.colors?.background
    if (newBgColor && newBgColor !== textColor && !isUpdatingWordSearchFromPicker) {
      setTextColor(newBgColor)
      // Update simpleBoxWrapper background color
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(selection)
        if (modelElement) {
          writer.setAttribute('backgroundColor', newBgColor, modelElement)
          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('background-color', newBgColor, viewElement)
              viewWriter.setStyle('background-color', newBgColor, viewElement)
            })
          }
        }
      })
    }

    if (wordSearchChild) {
      editor.model.change(writer => {
        const modelElement = editor.editing.mapper.toModelElement(wordSearchChild)
        if (modelElement) {
          writer.setAttribute('gameConfig', newConfigStr, modelElement)

          const viewElement = editor.editing.mapper.toViewElement(modelElement)
          if (viewElement) {
            editor.editing.view.change(viewWriter => {
              viewWriter.setAttribute('data-game-config', newConfigStr, viewElement)
            })
          }
        }
      })
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.box}>
        {/* Pixel Quest Options */}
        {isPixelQuest && (
          <>

            <div
              style={{ padding: '8px 0', width: '560px' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            >
              <JsonFormEngine
                data-cke-ignore-events="true"
                data={pixelQuestConfig || INITIAL_GAME_SCHEMA}
                onChange={onHandlePixelQuestConfigChange}
                onExternalChange={info => {
                  onHandlePixelQuestConfigChange(info.newData)
                }}
              />
            </div>
          </>
        )}

        {/* Crossword Puzzle Options */}
        {isCrossword && crosswordConfig && (
          <>
            <div
              style={{ padding: '8px 0', width: '560px' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            >
              <JsonFormEngine
                data-cke-ignore-events="true"
                data={crosswordConfig}
                onChange={onHandleCrosswordConfigChange}
                onExternalChange={info => {
                  onHandleCrosswordConfigChange(info.newData)
                }}
              />
            </div>
            <div style={{ marginTop: '8px', padding: '0 4px' }}>
              <Typography className={classes.helpText} style={{ fontStyle: 'italic' }}>
                Some words may be left out if they cannot connect or do not fit in the grid.
              </Typography>
            </div>
          </>
        )}

        {/* Riddle Options */}
        {isRiddle && (
          <>
            <div
              style={{ padding: '8px 0', width: '560px' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            >
              <JsonFormEngine
                data-cke-ignore-events="true"
                data={riddleConfig || INITIAL_RIDDLE_SCHEMA}
                onChange={onHandleRiddleConfigChange}
                onExternalChange={info => {
                  onHandleRiddleConfigChange(info.newData)
                }}
              />
            </div>
          </>
        )}

        {/* Wordle Options */}
        {isWordle && (
          <>
            <div
              style={{ padding: '8px 0', width: '560px' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            >
              <JsonFormEngine
                data-cke-ignore-events="true"
                data={wordleConfig || INITIAL_WORDLE_SCHEMA}
                onChange={onHandleWordleConfigChange}
                onExternalChange={info => {
                  onHandleWordleConfigChange(info.newData)
                }}
              />
            </div>
          </>
        )}

        {/* Jumble Options */}
        {isJumble && (
          <>
            <div
              style={{ padding: '8px 0', width: '560px' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            >
              <JsonFormEngine
                data-cke-ignore-events="true"
                data={jumbleConfig || INITIAL_JUMBLE_SCHEMA}
                onChange={onHandleJumbleConfigChange}
                onExternalChange={info => {
                  onHandleJumbleConfigChange(info.newData)
                }}
              />
            </div>
          </>
        )}

        {/* Word Search Options */}
        {isWordSearch && (
          <>
            <div
              style={{ padding: '8px 0', width: '560px' }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            >
              <JsonFormEngine
                data-cke-ignore-events="true"
                data={wordSearchConfig || INITIAL_WORD_SEARCH_SCHEMA}
                onChange={onHandleWordSearchConfigChange}
                onExternalChange={info => {
                  onHandleWordSearchConfigChange(info.newData)
                }}
              />
            </div>
          </>
        )}

        {/* Community Block Options */}
        {isCommunityBlock && (
          <>
            <div className={classes.communityRow}>
              <div className={classes.communityLabelContainer}>
                <Typography className={classes.header}>Moderation</Typography>
              </div>
              <FormControl size="small" className={classes.communitySelect}>
                <Select
                  variant="outlined"
                  value={moderation}
                  onChange={onHandleModerationChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxWidth: '340px',
                      },
                    },
                  }}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="aiAuto">AI Auto</MenuItem>
                  <MenuItem value="manually">Manually</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className={classes.communityHelpText}>
              <Typography className={classes.helpText}>
                Which posts and comments require approval?
              </Typography>
            </div>

            <div className={classes.communityRow}>
              <div className={classes.communityLabelContainer}>
                <Typography className={classes.header}>
                  Visitor permissions
                </Typography>
              </div>
              <FormControl size="small" className={classes.communitySelect}>
                <Select
                  variant="outlined"
                  value={visitorPermissions}
                  onChange={onHandleVisitorPermissionsChange}
                  renderValue={value => {
                    const labels = {
                      reader: 'Reader',
                      commenter: 'Commenter',
                      write: 'Writer',
                    }
                    return labels[value] || 'Writer'
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxWidth: '340px',
                      },
                    },
                  }}
                >
                  <MenuItem value="reader">
                    <div>
                      <Typography className={classes.menuItemTitle}>
                        Reader
                      </Typography>
                      <Typography className={classes.menuItemDesc}>
                        Visitors can access this board and read posts.
                      </Typography>
                    </div>
                  </MenuItem>
                  <MenuItem value="commenter">
                    <div>
                      <Typography className={classes.menuItemTitle}>
                        Commenter
                      </Typography>
                      <Typography className={classes.menuItemDesc}>
                        Visitors can comment on posts and add reactions.
                      </Typography>
                    </div>
                  </MenuItem>
                  <MenuItem value="write">
                    <div>
                      <Typography className={classes.menuItemTitle}>
                        Writer
                      </Typography>
                      <Typography className={classes.menuItemDesc}>
                        Visitors can write new posts.
                      </Typography>
                    </div>
                  </MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className={classes.communityHelpText}>
              <Typography className={classes.helpText}>
                Which posts and comments require approval?
              </Typography>
            </div>

            <div className={classes.communityRow}>
              <div className={classes.communityLabelContainer}>
                <Typography className={classes.header}>Language</Typography>
              </div>
              <FormControl size="small" className={classes.communitySelect}>
                <Select
                  variant="outlined"
                  value={communityLanguage}
                  onChange={onHandleCommunityLanguageChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        maxWidth: '340px',
                      },
                    },
                  }}
                >
                  {languages.map(lang => (
                    <MenuItem key={lang.countryCode} value={lang.countryCode}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className={classes.communityHelpText}>
              <Typography className={classes.helpText}>
                Select the language for community content.
              </Typography>
            </div>
          </>
        )}

        {/* Badge Block Options */}
        {isBadgeBlock && (
          <>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>
                  Theme Color
                </Typography>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {rewardThemeColors.map((gradient, index) => (
                  <div
                    key={index}
                    onClick={() => onHandleCompletionRewardBgColor(gradient)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: gradient,
                      cursor: 'pointer',
                      ...(completionRewardBgColor === gradient
                        ? { border: '2px solid #015FFB', boxSizing: 'border-box' }
                        : {}),
                    }}
                  />
                ))}
              </div>
            </div>

            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Start</Typography>
              </div>
              <input
                className={classes.badgeInput}
                value={startText}
                onChange={onHandleStartTextChange}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                onInput={e => e.stopPropagation()}
                onFocus={e => e.stopPropagation()}
                onBlur={e => e.stopPropagation()}
                data-cke-ignore-events="true"
              />
            </div>

            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Resume</Typography>
              </div>
              <input
                className={classes.badgeInput}
                value={resumeText}
                onChange={onHandleResumeTextChange}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                onInput={e => e.stopPropagation()}
                onFocus={e => e.stopPropagation()}
                onBlur={e => e.stopPropagation()}
                data-cke-ignore-events="true"
              />
            </div>
          </>
        )}

        {/* Continue Learning Block Options */}
        {isContinueLearningBlock && (
          <>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Title</Typography>
              </div>
              <input
                className={classes.badgeInput}
                value={clTitle}
                onChange={onHandleClTitleChange}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                onInput={e => e.stopPropagation()}
                onFocus={e => e.stopPropagation()}
                onBlur={e => e.stopPropagation()}
                data-cke-ignore-events="true"
              />
            </div>
          </>
        )}

        {/* Dynamic List için Column Count seçeneği */}
        {isDynamicList && (
          <div className={classes.row}>
            <div>
              <Typography className={classes.header}>Columns</Typography>
            </div>
            <CoreToggleButtonGroup
              value={columnCount}
              onChange={onHandleColumnChange}
              exclusive
              fullWidth
            >
              <CoreToggleButton value="1">
                <Typography className={classes.header}>1</Typography>
              </CoreToggleButton>
              <CoreToggleButton value="2">
                <Typography className={classes.header}>2</Typography>
              </CoreToggleButton>
              <CoreToggleButton value="3">
                <Typography className={classes.header}>3</Typography>
              </CoreToggleButton>
              <CoreToggleButton value="4">
                <Typography className={classes.header}>4</Typography>
              </CoreToggleButton>
              <CoreToggleButton value="5">
                <Typography className={classes.header}>5</Typography>
              </CoreToggleButton>
            </CoreToggleButtonGroup>
          </div>
        )}
        {isContinueLearningBlock && (
          <div className={classes.row}>
            <div>
              <Typography className={classes.header}>Link label</Typography>
            </div>
            <input
              className={classes.badgeInput}
              value={clLinkLabel}
              onChange={onHandleClLinkLabelChange}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              onInput={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              onBlur={e => e.stopPropagation()}
              data-cke-ignore-events="true"
            />
          </div>
        )}

        {isPriceTagActive && (
          <div className={classes.row}>
            <div>
              <Typography className={classes.header}>Show Price Tag</Typography>
            </div>
            <CustomizedSwitch
              onClick={onHandlePriceTagChange}
              checked={priceTagActive}
            />
          </div>
        )}

        {isButtonWrapper && (
          <>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Alignment</Typography>
              </div>
              <CoreToggleButtonGroup
                value={alignment}
                onChange={onHandleAlignment}
                exclusive
                fullWidth
              >
                <CoreToggleButton value="left">
                  <Icon
                    className={classes.icon}
                    icon="proicons:text-align-left"
                  />
                </CoreToggleButton>
                <CoreToggleButton value="center">
                  <Icon
                    className={classes.icon}
                    icon="proicons:text-align-center"
                  />
                </CoreToggleButton>
                <CoreToggleButton value="right">
                  <Icon
                    className={classes.icon}
                    icon="proicons:text-align-right"
                  />
                </CoreToggleButton>
              </CoreToggleButtonGroup>
            </div>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Button Width</Typography>
              </div>
              <CoreToggleButtonGroup
                value={buttonWidth}
                onChange={onHandleButtonWidth}
                exclusive
                fullWidth
              >
                <CoreToggleButton value="fit">
                  <Typography className={classes.header}>Auto</Typography>
                </CoreToggleButton>
                <CoreToggleButton value="full">
                  <Typography className={classes.header}>Full</Typography>
                </CoreToggleButton>
              </CoreToggleButtonGroup>
            </div>
          </>
        )}

        {!isPixelQuest && !isCrossword && !isRiddle && !isWordle && !isJumble && !isWordSearch && (
          <>
            <div className={classes.row}>
              <div>
                <Typography className={classes.header}>Background</Typography>
              </div>
              <CoreToggleButtonGroup
                value={background}
                onChange={onHandleBackground}
                exclusive
                fullWidth
              >
                <CoreToggleButton value="color">
                  <Typography className={classes.header}>Color</Typography>
                </CoreToggleButton>
                <CoreToggleButton value="image">
                  <Typography className={classes.header}>Image</Typography>
                </CoreToggleButton>
              </CoreToggleButtonGroup>
            </div>

            {background === 'color' ? (
              <div className={classes.row}>
                <div>
                  <Typography className={classes.header}>Color</Typography>
                </div>
                <div className={classes.boxIconButton}>
                  <div className={classes.inputItem}>
                    <div className={classes.pickerBg}>
                      <span
                        className={classes.pickerSpan}
                        style={{
                          backgroundColor: textColor,
                          zIndex: '999999 !important',
                        }}
                        onClick={event => {
                          setPickerVisibles({
                            ...pickerVisibles,
                            fontColor: !pickerVisibles.fontColor,
                          })
                          setAnchorEl(event.currentTarget)
                        }}
                      ></span>
                      <Popover
                        className={classes.popover}
                        id={pickerVisibles.fontColor ? 'fontColor' : undefined}
                        open={pickerVisibles.fontColor}
                        anchorEl={anchorEl}
                        onClose={() => {
                          setPickerVisibles({ ...pickerVisibles, fontColor: false })
                          setAnchorEl(null)
                        }}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <ChromePicker
                          disabled={!pageReady}
                          color={textColor}
                          onChange={event => onHandleColor(toCssColor(event))}
                        />
                      </Popover>
                    </div>
                  </div>
                  <Typography className={classes.header}>{textColor}</Typography>
                </div>
              </div>
            ) : (
              <div className={classes.box}>
                <div className={classes.row}>
                  <div>
                    <Typography className={classes.header}>Source</Typography>
                  </div>
                  {imageURL === '' ? (
                    <div onClick={handleImage} className={classes.boxSourceButton}>
                      <div className={classes.boxIcon}>
                        <Icon icon="proicons:photo" />
                      </div>
                      <div className={classes.uploadText}>
                        <Typography className={classes.logoText}>
                          Upload Image
                        </Typography>
                      </div>
                    </div>
                  ) : (
                    <div className={classes.boxSourceImage}>
                      <img
                        className={classes.sourceImage}
                        src={imageURL}
                        alt="img"
                      />
                      <Icon
                        onClick={onHandleImageDelete}
                        className={classes.sourceIcon}
                        icon="iconoir:trash"
                      />
                    </div>
                  )}
                </div>
                {imageURL !== '' && (
                  <div className={classes.box}>
                    <div className={classes.row}>
                      <div>
                        <Typography className={classes.header}>
                          Image fill
                        </Typography>
                      </div>
                      <FormControl size="small">
                        <Select
                          fullWidth
                          variant="outlined"
                          id="size-select"
                          className="select"
                          value={size}
                          onChange={onHandleSize}
                        >
                          {Object.keys(SizeType).map(key => (
                            <MenuItem value={key} key={key}>
                              {SizeType[key].name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <div className={classes.row}>
                      <div>
                        <Typography className={classes.header}>
                          Image position
                        </Typography>
                      </div>
                      <div className={classes.backgroudPosition}>
                        <CoreBackgroundPosition
                          position={imagePosition}
                          onPositionChange={onChangePosition}
                        />
                      </div>
                    </div>
                    <div className={classes.row}>
                      <Divider className={classes.divider} />
                    </div>
                    <div className={classes.row}>
                      <div className={classes.overlay}>
                        <Typography className={classes.headerOverlay}>
                          Use image overlay
                        </Typography>
                        <CustomizedSwitch
                          onClick={onHandleChange}
                          checked={isChecked}
                        />
                      </div>
                    </div>
                    <div className={classes.row}>
                      <div>
                        <Typography className={classes.header}>
                          Image overlay color
                        </Typography>
                      </div>
                      <div className={classes.boxIconButton}>
                        <div className={classes.inputItem}>
                          <div className={classes.pickerBg}>
                            <span
                              className={classes.pickerSpan}
                              style={{
                                backgroundColor: overlayColor,
                                zIndex: '999999 !important',
                              }}
                              onClick={event => {
                                setPickerVisibles({
                                  ...pickerVisibles,
                                  fontColor: !pickerVisibles.fontColor,
                                })
                                setAnchorEl(event.currentTarget)
                              }}
                            ></span>
                            <Popover
                              className={classes.popover}
                              id={
                                pickerVisibles.fontColor ? 'fontColor' : undefined
                              }
                              open={pickerVisibles.fontColor}
                              anchorEl={anchorEl}
                              onClose={() => {
                                setPickerVisibles({
                                  ...pickerVisibles,
                                  fontColor: false,
                                })
                                setAnchorEl(null)
                              }}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                              }}
                            >
                              <ChromePicker
                                disableAlpha
                                disabled={!pageReady}
                                color={overlayColor}
                                onChange={event => onHandleOverlayColor(event.hex)}
                              />
                            </Popover>
                          </div>
                        </div>
                        <Typography className={classes.header}>
                          {overlayColor}
                        </Typography>
                      </div>
                    </div>
                    <div className={classes.row}>
                      <div className={classes.overlay}>
                        <Typography className={classes.header}>
                          Overlay opacity
                        </Typography>
                      </div>
                      <CoreSlider
                        initialValue={opacityValue}
                        fullWidth
                        height={4}
                        color={'#015FFB'}
                        thumbSize={16}
                        disabled={false}
                        step={1}
                        onChange={handleSliderChange}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}





      </div>
    </div>
  )
}

export default withStyles(styles)(BlockOptionsModal)
